import { motion } from "framer-motion";
import { Instagram, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";

export const InstagramSection = () => {
  const instagramUrl = "https://www.instagram.com/inspirion.gym?igsh=MW42OHpic3Z4bjE2NA==";
  const instagramHandle = "@inspirion.gym";

  return (
    <section 
      id="instagram"
      data-testid="instagram-section"
      className="relative py-20 bg-dark-paper overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(255, 0, 255, 0.2) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex-1 text-center lg:text-left"
          >
            {/* Instagram Icon */}
            <div 
              className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-2xl"
              style={{
                background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                boxShadow: "0 0 30px rgba(225, 48, 108, 0.4)",
              }}
            >
              <Instagram className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-4">
              Síguenos en
              <br />
              <span className="text-neon-magenta text-glow-magenta">Instagram</span>
            </h2>
            
            <p className="text-gray-400 text-lg mb-6 max-w-md">
              Descubre transformaciones reales, tips de entrenamiento y el día a día en Inspirion GYM.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                asChild
                data-testid="instagram-follow-btn"
                className="group bg-gradient-to-r from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90 text-white font-bold text-base px-8 py-6 rounded-none uppercase tracking-wide"
              >
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                  <Instagram className="w-5 h-5 mr-2" />
                  Seguir {instagramHandle}
                  <ExternalLink className="w-4 h-4 ml-2 opacity-70" />
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Right Content - Instagram Embed Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 w-full max-w-md"
          >
            <a 
              href={instagramUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block group"
            >
              <div 
                className="relative bg-dark-card border border-white/10 p-6 hover:border-neon-magenta/50 transition-colors duration-300"
                style={{ boxShadow: "0 0 30px rgba(0, 0, 0, 0.5)" }}
              >
                {/* Profile Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div 
                    className="w-16 h-16 rounded-full p-0.5"
                    style={{
                      background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                    }}
                  >
                    <div className="w-full h-full rounded-full bg-dark-card p-0.5">
                      <img
                        src="https://customer-assets.emergentagent.com/job_gym-rancagua/artifacts/vax34u1r_Logotipo%20Inspirion%20GYM%20v1_Alta%20Calidad.png"
                        alt="Inspirion GYM"
                        className="w-full h-full object-contain rounded-full bg-black"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">inspirion.gym</span>
                      <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <span className="text-gray-400 text-sm">Inspirion GYM</span>
                  </div>
                </div>

                {/* Fake Grid Preview */}
                <div className="grid grid-cols-3 gap-1 mb-4">
                  {[
                    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=200&h=200&fit=crop",
                    "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=200&h=200&fit=crop",
                    "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=200&h=200&fit=crop",
                    "https://images.unsplash.com/photo-1574680096145-d05b474e2155?w=200&h=200&fit=crop",
                    "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=200&h=200&fit=crop",
                    "https://images.unsplash.com/photo-1549060279-7e168fcee0c2?w=200&h=200&fit=crop",
                  ].map((img, index) => (
                    <div 
                      key={index} 
                      className="aspect-square overflow-hidden bg-dark-paper"
                    >
                      <img
                        src={img}
                        alt={`Post ${index + 1}`}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                  ))}
                </div>

                {/* View Profile CTA */}
                <div className="text-center pt-4 border-t border-white/10">
                  <span className="text-neon-magenta font-semibold text-sm uppercase tracking-wider group-hover:text-white transition-colors">
                    Ver perfil completo →
                  </span>
                </div>

                {/* Hover overlay effect */}
                <div className="absolute inset-0 bg-neon-magenta/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InstagramSection;
